import { useEffect, useRef, useState } from 'react';
import { ControllerRenderProps } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogFooter,
    AlertDialogTitle,
    AlertDialogTrigger,
} from '@codes/ui-kit/components/ui/alert-dialog';
import { VerifyEmailStep } from '@lemoncloud/codes-backend-api';
import { WorkspaceBody } from '@lemoncloud/codes-goods-api';
import { ChevronRight, X } from 'lucide-react';

import { useVerifyEmail } from '@codes/auth';
import {
    Button,
    Input,
    Label,
    SharedSpinner,
    Sheet,
    SheetClose,
    SheetContent,
    SheetFooter,
    SheetHeader,
    SheetTitle,
    SheetTrigger,
} from '@codes/ui-kit';

import { EMAIL_VALIDATION, EmailError, EmailStep } from '../consts';

interface EmailValidationDialogProps {
    field: ControllerRenderProps<WorkspaceBody, 'request$.email'>;
    onEmailSubmit: (email: string) => void;
    disabled?: boolean;
}

export const EmailValidationDialog = ({ field, onEmailSubmit, disabled = false }: EmailValidationDialogProps) => {
    const { t } = useTranslation();

    const [email, setEmail] = useState('');
    const [code, setCode] = useState('');
    const [timeLeft, setTimeLeft] = useState(0);
    const [error, setError] = useState<EmailError | null>(null);

    const [step, setStep] = useState<VerifyEmailStep>(EmailStep.Send);
    const timerRef = useRef<NodeJS.Timeout | null>(null);

    const { mutateAsync: verifyEmail, isSending, isChecking, isResending, isPending: isVerifying } = useVerifyEmail();

    const startTimer = () => {
        setTimeLeft(EMAIL_VALIDATION.TIMER_DURATION);
        if (timerRef.current) {
            clearInterval(timerRef.current);
        }
        timerRef.current = setInterval(() => {
            setTimeLeft(prev => {
                if (prev <= 1) {
                    setCode('');
                    setStep(EmailStep.Send);
                    if (timerRef.current) {
                        clearInterval(timerRef.current);
                    }
                    return 0;
                }
                return prev - 1;
            });
        }, 1000);
    };

    const onGetCode = async () => {
        await verifyEmail({
            mode: 'workspace',
            step: EmailStep.Send,
            email,
        });
        setStep(EmailStep.Check);
        startTimer();
    };
    const onConfirm = async () => {
        const result = await verifyEmail({
            mode: 'workspace',
            step: EmailStep.Check,
            email,
            code,
        });

        if (result.error) {
            const isExpired = result.error.includes('expired');
            const isMismatched = result.error.includes('mismatched');
            if (isExpired) {
                setError(EmailError.Expired);
            } else if (isMismatched) {
                setError(EmailError.Mismatched);
            } else {
                setError(EmailError.Unknown);
            }
            return;
        }

        setStep(EmailStep.Confirm);
        setError(null);
        if (timerRef.current) {
            clearInterval(timerRef.current);
            setTimeLeft(0);
        }
    };
    const onResend = async () => {
        await verifyEmail({
            mode: 'workspace',
            step: EmailStep.Resend,
            email,
        });
        startTimer();
        setError(null);
        setCode('');
    };

    const onSubmit = () => {
        onEmailSubmit(email);
    };

    const isConfirmed = step === EmailStep.Confirm;
    const isChecked = step === EmailStep.Check;
    const isValidEmail = EMAIL_VALIDATION.EMAIL_REGEX.test(email);
    const isValidCode = !!code;

    const formatTime = (seconds: number) => {
        const mins = Math.floor(seconds / 60);
        const secs = seconds % 60;
        return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    };

    const [open, onOpenChange] = useState(false);

    useEffect(() => {
        const reset = () => {
            setStep('send');
            setCode('');
            setEmail('');
            setTimeLeft(0);
            setError(null);
            if (timerRef.current) {
                clearInterval(timerRef.current);
                timerRef.current = null;
            }
        };

        if (open) {
            reset();
        }

        return () => {
            if (timerRef.current) {
                clearInterval(timerRef.current);
            }
        };
    }, [open]);

    return (
        <Sheet open={open} onOpenChange={onOpenChange}>
            <SheetTrigger asChild>
                <Button
                    disabled={disabled}
                    type="button"
                    variant="outline"
                    className="border-border w-full flex justify-between pr-[10px] disabled:bg-default disabled:border-border disabled:text-text-400"
                >
                    {field.value ? (
                        <div>{field.value}</div>
                    ) : (
                        <>
                            <div className="text-text-400">
                                {t('workspace.createV2.emailValidation.emailPlaceholder')}
                            </div>
                            <ChevronRight className="w-4 h-4 text-sub" />
                        </>
                    )}
                </Button>
            </SheetTrigger>
            <SheetContent
                className="overflow-hidden"
                onInteractOutside={e => e.preventDefault()}
                onEscapeKeyDown={e => e.preventDefault()}
            >
                <SheetHeader>
                    <SheetTitle className="text-base">{t('workspace.createV2.emailValidation.title')}</SheetTitle>
                    <AlertDialog>
                        <AlertDialogTrigger asChild>
                            <button type="button" disabled={isVerifying}>
                                <X className="w-5 h-5" />
                            </button>
                        </AlertDialogTrigger>
                        <AlertDialogContent className="z-[1001]">
                            <AlertDialogTitle>
                                {t('workspace.createV2.emailValidation.confirmStopValidation')}
                            </AlertDialogTitle>
                            <AlertDialogFooter>
                                <AlertDialogCancel>{t('workspace.createV2.emailValidation.goBack')}</AlertDialogCancel>
                                <AlertDialogAction asChild>
                                    <SheetClose>{t('workspace.createV2.emailValidation.stop')}</SheetClose>
                                </AlertDialogAction>
                            </AlertDialogFooter>
                        </AlertDialogContent>
                    </AlertDialog>
                </SheetHeader>

                <div className="pt-5 pb-10 overflow-auto">
                    <div className="text-lg font-medium whitespace-pre-line">
                        {t('workspace.createV2.emailValidation.description')}
                    </div>
                    <div className="mt-8 space-y-4">
                        <div>
                            <Label variant="form">{t('workspace.createV2.emailLabel')}</Label>
                            <div className="flex items-center gap-2">
                                <Input
                                    placeholder={t('workspace.createV2.emailValidation.emailPlaceholder')}
                                    className="flex-1"
                                    type="email"
                                    value={email}
                                    disabled={isChecked || isConfirmed || isVerifying}
                                    onChange={e => setEmail(e.target.value)}
                                />
                                {step === 'send' ? (
                                    <Button
                                        variant="outline"
                                        className="min-w-[166px]"
                                        disabled={isVerifying || isConfirmed || !isValidEmail}
                                        onClick={onGetCode}
                                    >
                                        {isSending ? (
                                            <SharedSpinner />
                                        ) : (
                                            t('workspace.createV2.emailValidation.getCodeButton')
                                        )}
                                    </Button>
                                ) : (
                                    <Button
                                        variant="outline"
                                        className="min-w-[166px] text-link"
                                        disabled={isVerifying || isConfirmed}
                                        onClick={onResend}
                                    >
                                        {isResending ? (
                                            <SharedSpinner />
                                        ) : (
                                            t('workspace.createV2.emailValidation.resendButton')
                                        )}
                                    </Button>
                                )}
                            </div>
                        </div>

                        {(step === 'check' || step === 'confirm') && (
                            <div className="flex gap-2">
                                <div className="relative w-full">
                                    <Input
                                        placeholder={t('workspace.createV2.emailValidation.codePlaceholder')}
                                        value={code}
                                        onChange={e => setCode(e.target.value)}
                                        disabled={isConfirmed || isVerifying}
                                    />
                                    {!!error && (
                                        <div className="text-error text-xs font-medium pl-[6px] mt-2">
                                            {error === EmailError.Expired &&
                                                t('workspace.createV2.emailValidation.expiredError')}
                                            {error === EmailError.Mismatched &&
                                                t('workspace.createV2.emailValidation.mismatchedError')}
                                            {error === EmailError.Unknown &&
                                                t('workspace.createV2.emailValidation.unknownError')}
                                        </div>
                                    )}
                                    {timeLeft > 0 && (
                                        <div className="absolute right-4 top-0 translate-y-1/2 text-error text-xs font-medium">
                                            {formatTime(timeLeft)}
                                        </div>
                                    )}
                                </div>
                                <Button
                                    className="min-w-[166px]"
                                    onClick={onConfirm}
                                    disabled={isVerifying || isConfirmed || !isValidCode}
                                >
                                    {isChecking ? (
                                        <SharedSpinner />
                                    ) : (
                                        t('workspace.createV2.emailValidation.confirmButton')
                                    )}
                                </Button>
                            </div>
                        )}
                    </div>
                </div>
                <SheetFooter>
                    <SheetClose asChild>
                        <Button size="lg" className="w-full" disabled={!isConfirmed} onClick={onSubmit}>
                            {t('workspace.createV2.emailValidation.completedButton')}
                        </Button>
                    </SheetClose>
                </SheetFooter>
            </SheetContent>
        </Sheet>
    );
};
